import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { getChatwootSDK } from "@/integrations/chatwoot/sdk";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ChatwootAccess() {
  const [status, setStatus] = useState<"loading" | "missing" | "error">("loading");
  const [message, setMessage] = useState<string>("Preparando acesso ao Chatwoot...");
  const navigate = useNavigate();

  useEffect(() => {
    const openChatwoot = async () => {
      try {
        setStatus("loading");
        setMessage("Gerando link de acesso seguro...");

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("Sessão expirada. Faça login novamente.");

        const { data: profile } = await supabase
          .from("profiles")
          .select("chatwoot_user_id, chatwoot_user_token")
          .eq("id", user.id)
          .maybeSingle();

        if (!profile?.chatwoot_user_id && !profile?.chatwoot_user_token) {
          setStatus("missing");
          setMessage("Ainda não encontramos uma conta Chatwoot vinculada.");
          return;
        }

        const sdk = getChatwootSDK();
        const { url } = await sdk.getUserSsoLink(profile?.chatwoot_user_id || undefined);

        if (!url) {
          throw new Error("Não foi possível gerar o link de acesso ao Chatwoot.");
        }

        window.location.href = url;
      } catch (error: any) {
        console.error("Erro ao abrir Chatwoot", error);
        const friendly = error?.message || "Falha ao tentar acessar o Chatwoot.";
        setMessage(friendly);
        setStatus(error?.code === "missing" ? "missing" : "error");
        toast.error(friendly);
      }
    };

    void openChatwoot();
  }, []);

  const handleGoToSetup = () => navigate("/chatwoot");

  return (
    <div className="flex h-full items-center justify-center">
      <Card className="max-w-lg w-full mx-4">
        <CardHeader>
          <CardTitle>Abrindo Chatwoot</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {status === "loading" && (
            <div className="flex items-center gap-3 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>{message}</span>
            </div>
          )}

          {status === "missing" && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {message}
              </p>
              <Button onClick={handleGoToSetup} className="w-full">
                Configurar acesso ao Chatwoot
              </Button>
            </div>
          )}

          {status === "error" && (
            <div className="space-y-4">
              <p className="text-sm text-destructive">{message}</p>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button onClick={handleGoToSetup} variant="secondary" className="flex-1">
                  Revisar dados do Chatwoot
                </Button>
                <Button onClick={() => window.location.reload()} className="flex-1">
                  Tentar novamente
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
